import { useState, useEffect, useRef, useMemo } from "react";
import { Tooltip } from "antd";
import { calculateProposal } from "../services/rateCardService";
import { useActiveRateCard, useProposalTemplate, useProposalPackages } from "../hooks/useRateCard";
import { PROPOSAL_CSS } from "./proposalTheme";
import ProposalDocument from "./ProposalDocument";

/**
 * Proposal & Quotation — Phase 1.
 *
 * The form on the left, the finished document on the right. Every price comes
 * from POST /proposals/calculate and every other word from /proposal-template:
 * nothing about the quote is computed or authored in the browser, so a rate card
 * edit changes what reps quote immediately.
 *
 * Save / PDF / Send are Phase 2 — the buttons are visible but disabled so the
 * layout does not shift when they arrive.
 */

const DEBOUNCE_MS = 350;

const EMPTY_FORM = {
  segment: "corporate",
  client_name: "",
  contact_name: "",
  email: "",
  phone: "",
  gstin: "",
  address: "",
  marks: 800,
  package: "auto",
  users: 1,
  clients: 0,
  custom_rate: "",
  frequency: "quarterly",
  sales_discount: 0,
  onboarding_discount: 0,
  start_date: new Date().toISOString().slice(0, 10),
  notes: "",
};

/* Defaults worth showing when the segment flips — an attorney book is bigger and
   comes with more seats than a corporate portfolio. */
const SEGMENT_DEFAULTS = {
  corporate: { marks: 800, users: 1, clients: 0 },
  attorney: { marks: 1500, users: 2, clients: 90 },
};

const CreateProposal = () => {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [quote, setQuote] = useState(null);
  const [calcError, setCalcError] = useState("");
  const [calculating, setCalculating] = useState(false);

  const { data: rateCard } = useActiveRateCard();
  /* Asked per segment: the API resolves this segment's cover subtitle, offerings
     and summary body (falling back where a custom segment has none of its own),
     so the document does not have to guess. */
  const { data: template, isLoading: templateLoading } = useProposalTemplate(form.segment);
  const { data: packages = [] } = useProposalPackages(form.segment);

  const debounceRef = useRef(null);
  const requestRef = useRef(0);
  const docRef = useRef(null);

  /* The admin shell sets `overflow: hidden` on .main-content, and any ancestor
     with a clipping overflow disables position:sticky outright — which is why
     the form panel never followed the scroll. Lifted only while this screen is
     mounted, and put back on the way out so no other page is affected. */
  useEffect(() => {
    const shell = document.querySelector(".main-content");
    if (!shell) return undefined;
    const previous = shell.style.overflow;
    shell.style.overflow = "visible";
    return () => { shell.style.overflow = previous; };
  }, []);
  const [copied, setCopied] = useState(false);

  /* Copies the rendered document, not the React tree — the same thing the
     original's "Copy HTML" produced, so it can be pasted into an email. */
  const copyHtml = async () => {
    if (!docRef.current) return;
    try {
      await navigator.clipboard.writeText(docRef.current.innerHTML);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is blocked outside a secure context; nothing useful to say.
    }
  };

  const config = rateCard?.config || {};
  /* Segments come from the rate card, not a hardcoded pair: corporate and
     attorney are built in, anything else is a custom segment the admin added. */
  const customSegments = config.custom_segments || {};
  const segments = [
    { value: "corporate", label: "🏢 Corporate" },
    { value: "attorney", label: "⚖️ Attorney" },
    ...Object.keys(customSegments).map((slug) => ({
      value: slug,
      label: customSegments[slug]?.label || slug,
    })),
  ];
  /* Built-in limits sit at config.limits; a custom segment carries its own. */
  const limits = customSegments[form.segment]?.limits || config.limits?.[form.segment] || {};
  /* Clients are a per-trademark-model idea (seats + client book), not a flat-tier
     one — so the field follows the model, not the segment name. */
  const segmentModel =
    form.segment === "attorney" ? "per_tm"
    : form.segment === "corporate" ? "flat_tiers"
    : customSegments[form.segment]?.model || "flat_tiers";
  const usesClients = segmentModel === "per_tm";
  const maxSales = config.discounts?.max_sales ?? 20;
  const maxOnboarding = config.discounts?.max_onboarding ?? 100;

  /* Stable per visit: the quotation number must not change on every keystroke
     while the document re-renders. */
  const quoteNo = useMemo(
    () => `EF/Q/${new Date().getFullYear()}/${Math.floor(1000 + Math.random() * 9000)}`,
    [],
  );

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const switchSegment = (segment) => {
    if (segment === form.segment) return;
    /* A rate meant for another segment is meaningless here, so it is dropped.
       A custom segment has no shipped defaults — start it at its own minimum. */
    const defaults = SEGMENT_DEFAULTS[segment] || {
      marks: customSegments[segment]?.limits?.min_marks || 1,
      users: 1,
      clients: 0,
    };
    set({ segment, package: "auto", custom_rate: "", ...defaults });
  };

  const marksError = (() => {
    const m = Number(form.marks);
    if (!m) return "Enter the number of trademarks";
    if (limits.min_marks && m < limits.min_marks) return `Minimum ${limits.min_marks.toLocaleString("en-IN")} marks`;
    if (limits.max_marks && m > limits.max_marks) {
      // Above the ceiling the API answers with a custom quote, not an error.
      return null;
    }
    return null;
  })();

  /* The counter drops responses that arrive after a newer request, so fast
     typing cannot leave stale pricing on screen. */
  const runCalculate = async () => {
    const id = ++requestRef.current;
    setCalculating(true);
    try {
      const data = await calculateProposal({
        segment: form.segment,
        marks: Number(form.marks) || 0,
        package: form.package,
        users: Number(form.users) || 1,
        clients: usesClients ? Number(form.clients) || 0 : 0,
        custom_rate: form.custom_rate === "" ? null : Number(form.custom_rate),
        frequency: form.frequency,
        sales_discount: Number(form.sales_discount) || 0,
        onboarding_discount: Number(form.onboarding_discount) || 0,
      });
      if (id !== requestRef.current) return;
      setQuote(data);
      setCalcError("");
    } catch (err) {
      if (id !== requestRef.current) return;
      setQuote(null);
      setCalcError(err?.message || "Could not calculate this quote");
    } finally {
      if (id === requestRef.current) setCalculating(false);
    }
  };

  const recalculate = () => {
    if (marksError) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    runCalculate();
  };

  /* One debounced calculate per settled edit. */
  useEffect(() => {
    if (marksError) { setQuote(null); setCalcError(marksError); return undefined; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runCalculate, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.segment, form.marks, form.package, form.users, form.clients,
    form.custom_rate, form.frequency, form.sales_discount, form.onboarding_discount,
    marksError,
  ]);

  const recommended = quote?.line_items?.recommended_per_mark_rate;
  const rateTooLow =
    form.custom_rate !== "" && recommended && Number(form.custom_rate) < Number(recommended);

  const disabledBtn = {
    width: "100%", marginTop: 8, padding: 11, borderRadius: 11, border: "1px solid #E7E9F5",
    background: "#fff", color: "#8896A8", fontWeight: 700, fontSize: 13, cursor: "not-allowed",
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <style>{PROPOSAL_CSS}</style>

          <div className="pg-scope">
            <div className="app">

              {/* ── FORM ── */}
              <div className="formcol">
              <div className="panel">
                <div className="brand">
                  <span className="logo">E</span> {template?.company?.brand_name || "Entermark"}
                  <span style={{ color: "var(--mut2)", fontSize: 11, fontWeight: 600 }}>Proposal</span>
                </div>
                <p className="sub" style={{ marginTop: 10 }}>
                  Fill the inputs — pricing and the document update as you type.
                </p>

                {/* Two segments fit side by side; more than that wraps rather
                    than squeezing the labels. */}
                <div
                  className="seg"
                  style={segments.length > 2 ? { gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" } : undefined}
                >
                  {segments.map((s) => (
                    <button
                      key={s.value}
                      className={form.segment === s.value ? "on" : ""}
                      onClick={() => switchSegment(s.value)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>

                <label>Client / company name *</label>
                <input value={form.client_name} onChange={(e) => set({ client_name: e.target.value })} placeholder="e.g. NovaPay Technologies" />

                <div className="row2">
                  <div>
                    <label>Contact name *</label>
                    <input value={form.contact_name} onChange={(e) => set({ contact_name: e.target.value })} placeholder="Priya Sharma" />
                  </div>
                  <div>
                    <label>Contact email *</label>
                    <input value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="priya@novapay.in" />
                  </div>
                </div>

                <label>Contact phone *</label>
                <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="+91 98xxx xxxxx" />

                <div className="row2">
                  <div>
                    <label>Client GSTIN</label>
                    <input value={form.gstin} onChange={(e) => set({ gstin: e.target.value })} placeholder="optional" />
                  </div>
                  <div>
                    <label>Billing address</label>
                    <input value={form.address} onChange={(e) => set({ address: e.target.value })} placeholder="City, State, PIN" />
                  </div>
                </div>

                <label>
                  Trademarks{" "}
                  {limits.min_marks != null && (
                    <span style={{ fontWeight: 400, color: "var(--mut2)" }}>
                      ({limits.min_marks.toLocaleString("en-IN")} – {limits.max_marks?.toLocaleString("en-IN")})
                    </span>
                  )} *
                </label>
                <input type="number" value={form.marks} onChange={(e) => set({ marks: e.target.value })} />
                {marksError && <div className="warn">{marksError}</div>}

                <label>Package</label>
                <select value={form.package} onChange={(e) => set({ package: e.target.value })}>
                  {packages.length === 0
                    ? <option value="auto">Auto — recommend by count</option>
                    : packages.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>

                <label>Number of users / seats</label>
                <input type="number" min="1" value={form.users} onChange={(e) => set({ users: e.target.value })} />

                {usesClients && (
                  <>
                    <label>Clients</label>
                    <input type="number" min="0" value={form.clients} onChange={(e) => set({ clients: e.target.value })} />
                  </>
                )}

                <label>
                  Per-trademark rate ₹/mark/yr{" "}
                  {recommended > 0 && (
                    <span style={{ fontWeight: 400, color: "var(--mut2)" }}>
                      · min / recommended ₹{recommended}/mark/yr
                    </span>
                  )}
                </label>
                <input
                  type="number" min="0" value={form.custom_rate}
                  onChange={(e) => set({ custom_rate: e.target.value })}
                  placeholder="blank = recommended · can only go higher"
                />
                {rateTooLow && (
                  <div className="warn">A custom rate can only go above the recommended ₹{recommended}.</div>
                )}

                <label>Payment frequency</label>
                <select value={form.frequency} onChange={(e) => set({ frequency: e.target.value })}>
                  <option value="quarterly">Quarterly{config.discounts?.auto_quarterly ? ` — ${config.discounts.auto_quarterly}% auto discount` : ""}</option>
                  <option value="yearly">Yearly{config.discounts?.auto_yearly ? ` — ${config.discounts.auto_yearly}% auto discount` : ""}</option>
                </select>

                <label>Subscription start date</label>
                <input type="date" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} />

                <label>Additional discount % (max {maxSales} — rep discretion)</label>
                <input
                  type="number" min="0" max={maxSales} value={form.sales_discount}
                  onChange={(e) => set({ sales_discount: Math.min(Number(e.target.value) || 0, maxSales) })}
                />

                <label>Onboarding discount % (max {maxOnboarding} — can be waived fully)</label>
                <input
                  type="number" min="0" max={maxOnboarding} value={form.onboarding_discount}
                  onChange={(e) => set({ onboarding_discount: Math.min(Number(e.target.value) || 0, maxOnboarding) })}
                />

                <label>Notes for the proposal (optional)</label>
                <textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Any custom line…" />

                {/* The document already re-renders as you type; this is the
                    original's explicit trigger, kept for the case where a
                    debounced call was dropped by an error. */}
                <button className="btn" onClick={recalculate}>⚡ Generate proposal</button>

                <Tooltip title="Saving, PDF and sending arrive in the next phase">
                  <button style={disabledBtn} disabled>Save / Send — coming soon</button>
                </Tooltip>
              </div>
              </div>

              {/* ── PREVIEW ── */}
              <div className="stage">
                <div className="toolbar">
                  <span className="t">
                    Proposal preview
                    {calculating && <span style={{ color: "var(--ind)" }}> · calculating…</span>}
                  </span>
                  <div className="acts">
                    <button onClick={copyHtml}>{copied ? "Copied!" : "Copy HTML"}</button>
                    <button className="p" onClick={() => window.print()}>🖨 Print / Save PDF</button>
                  </div>
                </div>

                <div ref={docRef}>
                  {calcError && !marksError ? (
                    <div className="empty" style={{ color: "var(--red)" }}>{calcError}</div>
                  ) : templateLoading ? (
                    <div className="empty">Loading the document template…</div>
                  ) : (
                    <ProposalDocument
                      form={form} quote={quote} template={template} quoteNo={quoteNo}
                      segmentLabel={customSegments[form.segment]?.label}
                    />
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProposal;
